import { InsightsService } from '../../../insights-service/insights-service';
import { PageMeta } from '../../../insights-service/insights.models';

const HEARTBEAT_INTERVAL_MS = 10000;

export interface TimeTrackingHealthData {
  courseId: number;
  moduleId: number;
  pagegroup: string;
  pagename: string;
}

export interface TimeTrackingHealthSettings {
  heartbeatIntervalMs?: number;
  trackingServiceUrl: string;
  enabledLogging?: boolean;
  isTinCan?: boolean;
}

export interface TimeTrackingHealthServiceConfig {
  data: TimeTrackingHealthData;
  settings: TimeTrackingHealthSettings;
}

/**
 * A service to send time tracking heartbeats.
 */
export class TimeTrackingHealthService {
  private static instance: TimeTrackingHealthService;

  private _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private _config: TimeTrackingHealthServiceConfig | null = null;
  private _isInitialized = false;
  private _insightsService: InsightsService | null = null;

  // Private constructor to enforce singleton pattern.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  public static getInstance(): TimeTrackingHealthService {
    if (!TimeTrackingHealthService.instance) {
      TimeTrackingHealthService.instance = new TimeTrackingHealthService();
    }
    return TimeTrackingHealthService.instance;
  }

  private get _isDevMode(): boolean {
    return import.meta.env.MODE !== 'production';
  }

  private _log(...args: unknown[]): void {
    if (this._isDevMode) {
      // eslint-disable-next-line no-console
      console.log('[TimeTrackingHealthService]', ...args);
    }
  }

  /**
   * Initializes the time tracking health service.
   * @param config - The configuration object for the health service.
   * @param insightsService - An instance of the InsightsService.
   */
  public init(config: TimeTrackingHealthServiceConfig): void {
    if (this._isInitialized) {
      this._log('WARN: TimeTrackingHealthService is already initialized. Call teardown() first to re-initialize.');
      return;
    }

    this._log('Initializing TimeTrackingHealthService with config:', config);

    if (!config.settings) {
      throw new Error('TimeTrackingHealthService config is missing required settings');
    }
    this._config = config;

    this._insightsService = InsightsService.getInstance();

    if (!this._insightsService) {
      this._insightsService.init({
        trackingServiceUrl: config.settings.trackingServiceUrl,
        enabledLogging: config.settings.enabledLogging,
      });
    }

    this._isInitialized = true;
  }

  /**
   * Starts the time tracking heartbeat.
   */
  public start(): void {
    if (!this._isInitialized || !this._config) {
      this._log('ERROR: TimeTrackingHealthService is not initialized. Call init() before starting.');
      return;
    }

    const interval = this._config.settings.heartbeatIntervalMs ?? HEARTBEAT_INTERVAL_MS;
    this._log(`Starting time tracking heartbeat timer (${interval / 1000}s interval).`);
    this.sendHeartbeat(); // Send initial heartbeat immediately
    this._heartbeatTimer = setInterval(this.sendHeartbeat, interval);
    this.setupEventListeners();
  }

  /**
   * Stops the time tracking heartbeat.
   */
  public teardown(): void {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
      this._isInitialized = false;
      this._config = null;
      this._log('Cleared time tracking heartbeat timer.');
    }
    this.removeEventListeners();
  }

  private getPageMeta(): PageMeta {
    return {
      pagegroup: this._config.data.pagegroup,
      pagename: this._config.data.pagename,
    };
  }

  private sendHeartbeat = (): void => {
    if (!this._isInitialized || !this._config || !this._insightsService) {
      this._log('ERROR: Cannot send heartbeat, service not initialized.');
      return;
    }

    if (document.hasFocus() || this._config.settings.isTinCan) {
      this._log('Sending heartbeat.');
      this._insightsService.heartBeatEvent({
        ...this.getPageMeta(),
        course_id: this._config.data.courseId,
        module_id: this._config.data.moduleId,
        span_ms: this._config.settings.heartbeatIntervalMs ?? HEARTBEAT_INTERVAL_MS,
      });
    } else {
      this._log('Document does not have focus, skipping heartbeat.');
    }
  };

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this._log('Tab became visible, sending heartbeat.');
      this.sendHeartbeat();
    } else {
      this._log('Tab became hidden.');
    }
  };

  private setupEventListeners = (): void => {
    window.addEventListener('visibilitychange', this.handleVisibilityChange);
  };

  private removeEventListeners = (): void => {
    window.removeEventListener('visibilitychange', this.handleVisibilityChange);
  };
}
